import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { deviceDetection, performance } from '../utils/mobileUtils';

interface SmoothScrollSettings {
  enabled: boolean;
  strength: number; // Smoothness strength (0-1)
  threshold: number; // Minimum distance to trigger smooth scroll
  duration: number; // Animation duration in ms
  easing: (t: number) => number; // Easing function
  wheelMultiplier: number; // Mouse wheel sensitivity
  touchMultiplier: number; // Touch scroll sensitivity
  keyboardStep: number; // Keyboard scroll step size
}

interface ScrollPosition {
  x: number;
  y: number;
}

interface SmoothScrollContextType {
  settings: SmoothScrollSettings;
  scrollTo: (target: number | HTMLElement, options?: ScrollToOptions) => Promise<void>;
  scrollBy: (delta: number, options?: ScrollToOptions) => Promise<void>;
  getCurrentScroll: () => ScrollPosition;
  isScrolling: boolean;
  updateSettings: (newSettings: Partial<SmoothScrollSettings>) => void;
  disable: () => void;
  enable: () => void;
}

interface ScrollToOptions {
  duration?: number;
  easing?: (t: number) => number;
  offset?: number;
  behavior?: 'smooth' | 'instant';
}

const SmoothScrollContext = createContext<SmoothScrollContextType | null>(null);

// Easing functions
const easingFunctions = {
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeOutQuart: (t: number) => 1 - Math.pow(1 - t, 4),
  easeOutQuint: (t: number) => 1 - Math.pow(1 - t, 5),
  easeInOutCubic: (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  easeInOutQuart: (t: number) => t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2,
};

interface SmoothScrollProviderProps {
  children: ReactNode;
  disabled?: boolean;
  customSettings?: Partial<SmoothScrollSettings>;
}

export function SmoothScrollProvider({ 
  children, 
  disabled = false,
  customSettings = {} 
}: SmoothScrollProviderProps) {
  const [settings, setSettings] = useState<SmoothScrollSettings>({
    enabled: !disabled,
    strength: 0.8,
    threshold: 1,
    duration: 1000,
    easing: easingFunctions.easeOutCubic,
    wheelMultiplier: 1,
    touchMultiplier: 2,
    keyboardStep: 100,
    ...customSettings
  });

  const [isScrolling, setIsScrolling] = useState(false);
  const [currentScroll, setCurrentScroll] = useState({ x: 0, y: 0 });
  
  const rafRef = useRef<number>();
  const isAnimatingRef = useRef(false);
  const targetScrollRef = useRef(0);
  const startScrollRef = useRef(0);
  const startTimeRef = useRef(0);
  const animationDurationRef = useRef(1000);
  const easingRef = useRef(easingFunctions.easeOutCubic);

  // Check if smooth scrolling should be disabled based on device capabilities
  useEffect(() => {
    const checkSmoothScrollCapabilities = () => {
      // Keep native scrolling on touch/narrow viewports and when the user asks
      // for reduced motion. The viewport check also covers a desktop browser
      // resized to the mobile breakpoint.
      const isNarrowViewport = typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 767px)').matches
        : window.innerWidth < 768;
      if (deviceDetection.isMobile() || isNarrowViewport || deviceDetection.prefersReducedMotion()) {
        setSettings(current => ({ ...current, enabled: false }));
      }
    };

    checkSmoothScrollCapabilities();
  }, []);

  // Update scroll position tracking
  useEffect(() => {
    const updateScrollPosition = () => {
      setCurrentScroll({
        x: window.scrollX,
        y: window.scrollY
      });
    };

    window.addEventListener('scroll', updateScrollPosition, { passive: true });
    updateScrollPosition(); // Initial position

    return () => {
      window.removeEventListener('scroll', updateScrollPosition);
    };
  }, []);

  // Smooth scroll animation function
  const animate = useCallback((timestamp: number) => {
    if (!isAnimatingRef.current) return;

    const progress = Math.min((timestamp - startTimeRef.current) / animationDurationRef.current, 1);
    const easedProgress = easingRef.current(progress);
    
    const currentY = startScrollRef.current + (targetScrollRef.current - startScrollRef.current) * easedProgress;
    
    window.scrollTo(0, currentY);
    
    if (progress < 1) {
      rafRef.current = requestAnimationFrame(animate);
    } else {
      isAnimatingRef.current = false;
      setIsScrolling(false);
    }
  }, []);

  // Stop current animation
  const stopAnimation = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    isAnimatingRef.current = false;
    setIsScrolling(false);
  }, []);

  // Scroll to target position
  const scrollTo = useCallback(async (
    target: number | HTMLElement, 
    options: ScrollToOptions = {}
  ): Promise<void> => {
    const prefersReducedMotion = deviceDetection.prefersReducedMotion();
    const requestedBehavior = options.behavior || 'smooth';
    const behavior = prefersReducedMotion ? 'instant' : requestedBehavior;

    if (!settings.enabled) {
      // Fall back to native scroll
      if (typeof target === 'number') {
        window.scrollTo({ top: target, behavior: behavior === 'instant' ? 'auto' : behavior });
      } else {
        target.scrollIntoView({ behavior: behavior === 'instant' ? 'auto' : behavior });
      }
      return;
    }

    const {
      duration = settings.duration,
      easing = settings.easing,
      offset = 0,
    } = options;

    if (behavior === 'instant') {
      stopAnimation();
      if (typeof target === 'number') {
        window.scrollTo(0, target + offset);
      } else {
        const rect = target.getBoundingClientRect();
        const targetY = window.scrollY + rect.top + offset;
        window.scrollTo(0, targetY);
      }
      return;
    }

    return new Promise((resolve) => {
      stopAnimation();

      const startY = window.scrollY;
      let targetY: number;

      if (typeof target === 'number') {
        targetY = target + offset;
      } else {
        const rect = target.getBoundingClientRect();
        targetY = startY + rect.top + offset;
      }

      // Don't animate if already at target
      if (Math.abs(targetY - startY) < settings.threshold) {
        resolve();
        return;
      }

      startScrollRef.current = startY;
      targetScrollRef.current = targetY;
      animationDurationRef.current = duration;
      easingRef.current = easing;
      startTimeRef.current = performance.now();
      isAnimatingRef.current = true;
      setIsScrolling(true);

      rafRef.current = requestAnimationFrame(animate);

      // Resolve promise when animation completes
      setTimeout(() => {
        resolve();
      }, duration);
    });
  }, [settings, animate, stopAnimation]);

  // Scroll by delta amount
  const scrollBy = useCallback(async (
    delta: number, 
    options: ScrollToOptions = {}
  ): Promise<void> => {
    const targetY = window.scrollY + delta;
    return scrollTo(targetY, options);
  }, [scrollTo]);

  // Get current scroll position
  const getCurrentScroll = useCallback((): ScrollPosition => {
    return currentScroll;
  }, [currentScroll]);

  // Update settings
  const updateSettings = useCallback((newSettings: Partial<SmoothScrollSettings>) => {
    setSettings(current => ({ ...current, ...newSettings }));
  }, []);

  // Disable smooth scrolling
  const disable = useCallback(() => {
    stopAnimation();
    updateSettings({ enabled: false });
  }, [stopAnimation, updateSettings]);

  // Enable smooth scrolling
  const enable = useCallback(() => {
    updateSettings({ enabled: true });
  }, [updateSettings]);

  // Note: We intentionally don't intercept wheel events to preserve native scrolling behavior.
  // Smooth scrolling is only applied to programmatic scrolls (navigation clicks, scrollTo calls).
  // This provides the best user experience - smooth animations for intentional navigation
  // while maintaining responsive, native wheel/trackpad scrolling.

  // Set up keyboard navigation - only when not in interactive elements
  useEffect(() => {
    if (!settings.enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isAnimatingRef.current) return;
      if (e.defaultPrevented || document.body.classList.contains('drawer-open')) return;

      // Don't interfere with keyboard navigation in interactive elements
      const target = e.target as HTMLElement;
      if (target?.closest('[role="dialog"], [aria-modal="true"], [data-drawer]')) return;
      if (target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable ||
        target.closest('[role="textbox"]') ||
        target.closest('[contenteditable]')
      )) {
        return;
      }

      let delta = 0;

      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
          delta = e.key === 'PageDown' ? window.innerHeight * 0.8 : settings.keyboardStep;
          break;
        case 'ArrowUp':
        case 'PageUp':
          delta = -(e.key === 'PageUp' ? window.innerHeight * 0.8 : settings.keyboardStep);
          break;
        case 'Home':
          if (!e.ctrlKey) return; // Only handle Ctrl+Home for page navigation
          scrollTo(0);
          e.preventDefault();
          return;
        case 'End':
          if (!e.ctrlKey) return; // Only handle Ctrl+End for page navigation
          scrollTo(document.body.scrollHeight);
          e.preventDefault();
          return;
        default:
          return;
      }

      if (delta !== 0) {
        e.preventDefault();
        scrollBy(delta, {
          duration: settings.duration * 0.7,
          easing: easingFunctions.easeOutCubic
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [settings, scrollBy, scrollTo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAnimation();
    };
  }, [stopAnimation]);

  const value: SmoothScrollContextType = {
    settings,
    scrollTo,
    scrollBy,
    getCurrentScroll,
    isScrolling,
    updateSettings,
    disable,
    enable
  };

  return (
    <SmoothScrollContext.Provider value={value}>
      {children}
    </SmoothScrollContext.Provider>
  );
}

export function useSmoothScroll(): SmoothScrollContextType {
  const context = useContext(SmoothScrollContext);
  if (!context) {
    throw new Error('useSmoothScroll must be used within a SmoothScrollProvider');
  }
  return context;
}

// Hook for scroll-to-element functionality
export function useScrollToElement() {
  const { scrollTo } = useSmoothScroll();

  const scrollToElement = useCallback((
    selector: string | HTMLElement,
    options?: ScrollToOptions
  ) => {
    const element = typeof selector === 'string' 
      ? document.querySelector(selector) as HTMLElement
      : selector;

    if (element) {
      return scrollTo(element, options);
    }
    
    console.warn('Element not found for scrolling:', selector);
    return Promise.resolve();
  }, [scrollTo]);

  return { scrollToElement };
}

// Hook for scroll restoration
export function useScrollRestoration(key: string) {
  const { scrollTo, getCurrentScroll } = useSmoothScroll();

  const saveScrollPosition = useCallback(() => {
    const position = getCurrentScroll();
    sessionStorage.setItem(`scroll-${key}`, JSON.stringify(position));
  }, [key, getCurrentScroll]);

  const restoreScrollPosition = useCallback(() => {
    const saved = sessionStorage.getItem(`scroll-${key}`);
    if (saved) {
      try {
        const position = JSON.parse(saved) as ScrollPosition;
        scrollTo(position.y, { behavior: 'instant' });
      } catch (e) {
        console.warn('Failed to restore scroll position:', e);
      }
    }
  }, [key, scrollTo]);

  useEffect(() => {
    // Restore on mount
    restoreScrollPosition();

    // Save before unload
    window.addEventListener('beforeunload', saveScrollPosition);

    return () => {
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, [saveScrollPosition, restoreScrollPosition]);

  return {
    saveScrollPosition,
    restoreScrollPosition
  };
}
